import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Delete as DeleteIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import codexTypeService from '../services/codexTypeService';
import processService from '../services/processService';
import RegistryFieldPicker, { defaultFetchLocalEntryRegistries, shareRegistryFetch } from '../components/RegistryFieldPicker';
import RegistryBindingsEditor from '../components/RegistryBindingsEditor';
import {
  MSG_KEY_SEMANTICS,
  buildSchema,
  checkSchemaText,
  formatBindingTarget,
  registryStatus,
  registryTargetError,
  splitSchema,
  validateBindings,
  withBindings
} from '../utils/registryBindings';
import { MONO_FONT } from '../constants/typography';
import useCan from '../hooks/useCan';

// Attribute data types
const ATTRIBUTE_DATA_TYPES = [
  { value: 'STRING', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE', label: 'Date' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'JSON', label: 'JSON' },
  { value: 'REGISTRY_LOOKUP', label: 'Registry Lookup' }
];

const SAVE_ERROR_GENERIC = 'Salvataggio del tipo codex non riuscito. Riprova.';
const ATTRIBUTE_ERROR_RE = /^attributes\[([^\]]+)\]/;
const BINDING_ERROR_RE = /^attributes\[([^\]]+)\]\.registryBindings\[(\d+)\]/;
// errori sul lookup o sull'intera lista dei binding (senza indice, es. binding su un attributo non JSON)
const ATTRIBUTE_LEVEL_ERROR_RE = /^attributes\[([^\]]+)\]\.(?:registryLookup|registryBindings)\b/;

// `binding «committente»` (or `binding «n° 2»` while the path is still empty)
const bindingLabel = (binding, index) => `binding «${(binding?.path || '').trim() || `n° ${index + 1}`}»`;

// Readable line for a backend fieldError; binding indices resolve against the submitted attributes.
const describeFieldError = ({ field = '', message }, attributes) => {
  const binding = BINDING_ERROR_RE.exec(field);
  if (binding) {
    const [, code, i] = binding;
    const attr = attributes.find((a) => a.code === code);
    const index = Number(i);
    return `Attributo «${code}», ${bindingLabel(splitSchema(attr?.schema).bindings[index], index)}: ${message}`;
  }
  const attributeLevel = ATTRIBUTE_LEVEL_ERROR_RE.exec(field);
  if (attributeLevel) return `Attributo «${attributeLevel[1]}»: ${message}`;
  return `${field}: ${message}`;
};

const NewCodexType = ({ fetchRegistries = defaultFetchLocalEntryRegistries }) => {
  const can = useCan();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  // La rotta si apre in lettura (dettaglio) o con il permesso di creazione (nuovo): il
  // salvataggio chiede il permesso della chiamata che fa, altrimenti porta a un 403.
  const puoSalvare = can(isEdit ? 'codex_types.update' : 'codex_types.create');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [selectedProcesses, setSelectedProcesses] = useState([]);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    attributes: []
  });

  // Raw JSON text for each process configuration (keyed by processId)
  const [configTexts, setConfigTexts] = useState({});
  const [configErrors, setConfigErrors] = useState({});

  // Temporary state for new attribute
  const [newAttribute, setNewAttribute] = useState({
    code: '',
    description: '',
    dataType: 'STRING',
    isRequired: false,
    schema: null
  });

  // JSON attribute being created: schema text (without registryBindings) and
  // bindings are two independent controlled states, merged on "Aggiungi Attributo".
  const [schemaText, setSchemaText] = useState('');
  const [bindings, setBindings] = useState([]);
  // Bindings dialog for an existing attribute: { index, draft } | null
  const [bindingsDialog, setBindingsDialog] = useState(null);

  // Codes of the attributes named by the last save's fieldErrors (cleared on the next attempt)
  const [errorCodes, setErrorCodes] = useState([]);
  const submittingRef = useRef(false);

  // Internal registries (null until loaded), fetched once and shared with every picker: they
  // tell whether a target registry exists and which field is its key (bindings and lookups
  // must point at it). A failed fetch is retried by the next picker and still lands here.
  const [registries, setRegistries] = useState(null);
  // Non-blocking warning when the registries could not be loaded (cleared by a later successful fetch)
  const [registriesWarning, setRegistriesWarning] = useState(null);
  const sharedFetchRegistries = useMemo(() => shareRegistryFetch(async () => {
    const list = await fetchRegistries();
    const loaded = Array.isArray(list) ? list : [];
    setRegistries(loaded);
    setRegistriesWarning(null);
    return loaded;
  }), [fetchRegistries]);

  // La rotta chiede solo `codex_types.read`: operator, validator e viewer non hanno
  // `registries.read`, per loro l'elenco non si chiede e i nomi ripiegano sull'id.
  const puoLeggereAnagrafiche = can('registries.read');
  useEffect(() => {
    if (!puoLeggereAnagrafiche) return;
    sharedFetchRegistries().catch((err) => {
      // Una lettura negata (403) lascia l'elenco vuoto: non e' un guasto da segnalare.
      if (err?.isForbidden) return;
      setRegistriesWarning('Impossibile caricare le anagrafiche: riprova più tardi');
    });
  }, [sharedFetchRegistries, puoLeggereAnagrafiche]);

  const statusOf = (registryId) => registryStatus(registries, registryId);
  const nameOf = (registryId) => {
    const registry = statusOf(registryId).registry;
    return registry ? (registry.name || registry.code) : null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch available processes
        const processes = await processService.getAll();
        setAvailableProcesses(processes);

        // If in edit mode, fetch codex type data
        if (isEdit) {
          const codexType = await codexTypeService.getById(id);
          setFormData({
            code: codexType.code || '',
            description: codexType.description || '',
            attributes: codexType.attributes || []
          });
          const typeProcesses = codexType.codexTypeProcesses || [];
          setSelectedProcesses(typeProcesses);
          const texts = {};
          typeProcesses.forEach(p => {
            const config = p.configuration;
            texts[p.processId] = config != null
              ? JSON.stringify(config, null, 2)
              : '';
          });
          setConfigTexts(texts);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle new attribute field change
  const handleNewAttributeChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'dataType') {
      // A type change starts the type-specific config from scratch.
      setSchemaText('');
      setBindings([]);
      setNewAttribute(prev => ({ ...prev, dataType: value, schema: null }));
      return;
    }
    setNewAttribute(prev => ({
      ...prev,
      [name]: name === 'isRequired' ? checked : value
    }));
  };

  const isJsonAttribute = newAttribute.dataType === 'JSON';
  const schemaTextError = isJsonAttribute ? checkSchemaText(schemaText) : null;
  const bindingsValid = !isJsonAttribute || validateBindings(bindings, statusOf).valid;
  const lookup = newAttribute.schema?.registryLookup;
  const lookupValid = newAttribute.dataType !== 'REGISTRY_LOOKUP' || Boolean(lookup?.registryId && lookup?.field
    && !registryTargetError(statusOf(lookup.registryId), lookup.field));
  const canAddAttribute = Boolean(newAttribute.code && newAttribute.description)
    && !schemaTextError && bindingsValid && lookupValid;

  // Add attribute to document type form
  const handleAddAttribute = () => {
    // Validation
    if (!newAttribute.code || !newAttribute.description) {
      setError('The attribute code and description are required');
      return;
    }
    if (!canAddAttribute) return;

    const schema = isJsonAttribute ? buildSchema(schemaText, bindings) : newAttribute.schema;

    // Add the new attribute
    const newField = {
      ...newAttribute,
      schema,
      codexTypeId: formData.id
    };

    setFormData(prev => ({
      ...prev,
      attributes: [...prev.attributes, newField]
    }));

    // Reset the field
    setNewAttribute({
      code: '',
      description: '',
      dataType: 'STRING',
      isRequired: false,
      schema: null
    });
    setSchemaText('');
    setBindings([]);
  };

  // Bindings dialog (existing attributes): edits only schema.registryBindings
  const openBindingsDialog = (index) => {
    setBindingsDialog({ index, draft: splitSchema(formData.attributes[index]?.schema).bindings });
  };

  const handleConfirmBindings = () => {
    const { index, draft } = bindingsDialog;
    setFormData(prev => ({
      ...prev,
      attributes: prev.attributes.map((attr, i) =>
        i === index ? { ...attr, schema: withBindings(attr.schema, draft) } : attr
      )
    }));
    setBindingsDialog(null);
  };

  const renderBindingsChip = (attr, index) => {
    const current = splitSchema(attr.schema).bindings;
    // Same checks as the dialog's "Conferma": an invalid binding is visible without opening it.
    const { rows } = validateBindings(current, statusOf);
    const firstInvalid = rows.findIndex((row) => row.path || row.target);
    const errorText = firstInvalid >= 0
      ? `${bindingLabel(current[firstInvalid], firstInvalid)}: ${rows[firstInvalid].path || rows[firstInvalid].target}`
      : null;
    const label = current.length > 0 ? `${current.length} binding` : 'Nessun binding';
    const chip = (
      <Chip
        size="small"
        clickable
        variant="outlined"
        color={errorText ? 'error' : (current.length > 0 ? 'primary' : 'default')}
        icon={<LinkIcon />}
        label={label}
        aria-label={errorText ? `${label} — ${errorText}` : undefined}
        onClick={() => openBindingsDialog(index)}
      />
    );
    if (current.length === 0) return chip;
    return (
      <Tooltip
        title={(
          <Box component="span" sx={{ fontFamily: MONO_FONT, fontSize: '0.75rem', whiteSpace: 'pre-line' }}>
            {current.map((binding) => formatBindingTarget(binding, nameOf)).join('\n')}
            {errorText && `\n\n${errorText}`}
          </Box>
        )}
      >
        {chip}
      </Tooltip>
    );
  };

  // Legacy REGISTRY_LOOKUP on a non-key field: the backend rejects it, so flag it with a one-click fix.
  const handleUseLookupKey = (index, key) => {
    setFormData(prev => ({
      ...prev,
      attributes: prev.attributes.map((attr, i) => (i === index
        ? { ...attr, schema: { ...attr.schema, registryLookup: { ...attr.schema.registryLookup, field: key } } }
        : attr))
    }));
  };

  const renderLookupDetails = (attr, index) => {
    const { registryId, field } = attr.schema.registryLookup;
    const status = statusOf(registryId);
    const problem = registryTargetError(status, field);
    const caption = (
      <Typography variant="caption">
        {`${nameOf(registryId) || registryId} · ${field}`}
      </Typography>
    );
    if (!problem) return caption;
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        {caption}
        <Typography variant="caption" color="error">{problem}</Typography>
        {status.state === 'ok' && (
          <>
            <Button
              size="small"
              aria-label={`Usa la chiave per l'attributo «${attr.code}»`}
              onClick={() => handleUseLookupKey(index, status.key)}
            >
              Usa la chiave
            </Button>
            <Typography variant="caption" color="text.secondary">{MSG_KEY_SEMANTICS(status.key)}</Typography>
          </>
        )}
      </Stack>
    );
  };

  // Remove attribute from document type form
  const handleRemoveAttribute = (index) => {
    setFormData(prev => ({
      ...prev,
      attributes: prev.attributes.filter((_, i) => i !== index)
    }));
  };

  // Handle process selection
  const handleProcessSelection = (processId) => {
    setSelectedProcesses(prev => {
      const isSelected = prev.some(p => p.processId === processId);
      if (isSelected) {
        // Clean up config state for removed process
        setConfigTexts(ct => { const next = {...ct}; delete next[processId]; return next; });
        setConfigErrors(ce => { const next = {...ce}; delete next[processId]; return next; });
        // Remove the process and reorder the remaining ones
        return prev
          .filter(p => p.processId !== processId)
          .map((p, index) => ({
            ...p,
            processExecutionLevel: index + 1
          }));
      } else {
        const process = availableProcesses.find(p => p.id === processId);
        // Add new process with the next available execution level
        const maxLevel = prev.length > 0 ? Math.max(...prev.map(p => p.processExecutionLevel)) : 0;
        setConfigTexts(ct => ({ ...ct, [processId]: '' }));
        return [...prev, {
          processId: process.id,
          processExecutionLevel: maxLevel + 1,
          configuration: null
        }];
      }
    });
  };

  // Handle process reordering
  const handleProcessReorder = (processId, newLevel) => {
    setSelectedProcesses(prev => {
      const updatedProcesses = [...prev];
      const currentIndex = updatedProcesses.findIndex(p => p.processId === processId);
      const currentProcess = updatedProcesses[currentIndex];
      
      // Remove the process from its current position
      updatedProcesses.splice(currentIndex, 1);
      
      // Insert it at the new position
      updatedProcesses.splice(newLevel - 1, 0, {
        ...currentProcess,
        processExecutionLevel: newLevel
      });
      
      // Update execution levels for all processes
      return updatedProcesses.map((p, index) => ({
        ...p,
        processExecutionLevel: index + 1
      }));
    });
  };

  // Handle configuration JSON change for a process
  const handleConfigChange = (processId, text) => {
    setConfigTexts(prev => ({ ...prev, [processId]: text }));

    if (!text.trim()) {
      setConfigErrors(prev => { const next = {...prev}; delete next[processId]; return next; });
      setSelectedProcesses(prev =>
        prev.map(p => p.processId === processId ? { ...p, configuration: null } : p)
      );
      return;
    }

    try {
      const parsed = JSON.parse(text);
      setConfigErrors(prev => { const next = {...prev}; delete next[processId]; return next; });
      setSelectedProcesses(prev =>
        prev.map(p => p.processId === processId ? { ...p, configuration: parsed } : p)
      );
    } catch (e) {
      setConfigErrors(prev => ({ ...prev, [processId]: 'JSON non valido' }));
    }
  };

  const handleSubmit = async () => {
    // `loading` disables "Salva" only after a re-render: quick clicks must not send twice.
    if (submittingRef.current) return;
    setErrorCodes([]);
    // Validate all configuration fields are valid JSON
    if (Object.keys(configErrors).length > 0) {
      setError('Alcune configurazioni dei processi contengono JSON non valido. Correggi prima di salvare.');
      return;
    }

    try {
      submittingRef.current = true;
      setLoading(true);
      setError(null);

      const codexTypeData = {
        ...formData,
        codexTypeProcesses: selectedProcesses
      };

      if (isEdit) {
        await codexTypeService.update(id, codexTypeData);
      } else {
        await codexTypeService.create(codexTypeData);
      }

      navigate('/codex-types');
    } catch (err) {
      console.error('Error saving codex type:', err);
      const data = err.response?.data;
      const fieldErrors = Array.isArray(data?.fieldErrors) ? data.fieldErrors : [];
      if (fieldErrors.length > 0) {
        setError(fieldErrors.map((fieldError) => describeFieldError(fieldError, formData.attributes)));
        setErrorCodes(fieldErrors
          .map((fieldError) => ATTRIBUTE_ERROR_RE.exec(fieldError.field || '')?.[1])
          .filter(Boolean));
      } else {
        setError(data?.message || SAVE_ERROR_GENERIC);
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  if (loading && !formData.code) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {isEdit ? 'Modifica Tipo Codex' : 'Nuovo Tipo Codex'}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/codex-types')}
          >
            Annulla
          </Button>
          {puoSalvare && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !formData.code}
            >
              {loading ? <CircularProgress size={24} /> : 'Salva'}
            </Button>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {Array.isArray(error) ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {error.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </Box>
          ) : error}
        </Alert>
      )}

      {registriesWarning && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {registriesWarning}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Codice"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              disabled={isEdit}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Descrizione"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={2}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Attributi
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {formData.attributes.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                      <TableCell>Codice</TableCell>
                      <TableCell>Descrizione</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Obbligatorio</TableCell>
                      <TableCell>Dettagli</TableCell>
                      <TableCell align="right">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.attributes.map((attr, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          {errorCodes.includes(attr.code) ? (
                            <Box
                              component="span"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'error.main', fontWeight: 600 }}
                            >
                              {attr.code}
                              <ErrorOutlineIcon fontSize="inherit" titleAccess="Attributo con errori" />
                            </Box>
                          ) : attr.code}
                        </TableCell>
                        <TableCell>{attr.description}</TableCell>
                        <TableCell>{attr.dataType}</TableCell>
                        <TableCell>
                          {attr.isRequired ? 
                            <Chip size="small" label="SI" color="error" /> : 
                            <Chip size="small" label="NO" color="success" />
                          }
                        </TableCell>
                        <TableCell>
                          {attr.dataType === 'JSON'
                            ? (
                              <Stack direction="row" spacing={1} alignItems="center">
                                {splitSchema(attr.schema).schemaText && (
                                  <Button size="small" variant="outlined" startIcon={<CodeIcon />}>
                                    Schema
                                  </Button>
                                )}
                                {renderBindingsChip(attr, index)}
                              </Stack>
                            )
                            : attr.dataType === 'REGISTRY_LOOKUP' && attr.schema?.registryLookup
                              ? renderLookupDetails(attr, index)
                              : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Elimina attributo"
                            onClick={() => handleRemoveAttribute(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Nessun attributo definito.
              </Typography>
            )}
            
            <Box sx={{ p: 2, border: '1px dashed #ddd', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Aggiungi Nuovo Attributo
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Codice Attributo"
                    name="code"
                    value={newAttribute.code}
                    onChange={handleNewAttributeChange}
                    helperText="Usato come chiave nei metadati (camelCase, no spazi)"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Descrizione"
                    name="description"
                    value={newAttribute.description}
                    onChange={handleNewAttributeChange}
                    helperText="Descrizione user-friendly"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required size="small">
                    <InputLabel id="new-attribute-type-label">Tipo Dati</InputLabel>
                    <Select
                      labelId="new-attribute-type-label"
                      name="dataType"
                      value={newAttribute.dataType}
                      label="Tipo Dati"
                      onChange={handleNewAttributeChange}
                    >
                      {ATTRIBUTE_DATA_TYPES.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newAttribute.isRequired}
                        onChange={handleNewAttributeChange}
                        name="isRequired"
                        color="primary"
                        size="small"
                      />
                    }
                    label="Campo Obbligatorio"
                  />
                </Grid>
                
                {isJsonAttribute && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Definizione Schema JSON
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Inserisci uno schema JSON valido che definisce la struttura di questo attributo JSON.
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      label="Schema JSON"
                      name="schema"
                      value={schemaText}
                      onChange={(e) => setSchemaText(e.target.value)}
                      error={Boolean(schemaTextError)}
                      placeholder={`{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "type": "object",
  "properties": {
    "property1": {
      "type": "string",
      "description": "Descrizione di property1"
    },
    "property2": {
      "type": "number",
      "description": "Descrizione di property2"
    }
  },
  "required": ["property1"]
}`}
                      helperText={schemaTextError || 'Inserisci uno schema JSON valido per definire la struttura di questo attributo'}
                      size="small"
                      variant="outlined"
                      InputProps={{
                        sx: { fontFamily: MONO_FONT }
                      }}
                    />
                    <Box sx={{ mt: 3 }}>
                      <RegistryBindingsEditor
                        bindings={bindings}
                        onChange={setBindings}
                        fetchRegistries={sharedFetchRegistries}
                      />
                    </Box>
                  </Grid>
                )}

                {newAttribute.dataType === 'REGISTRY_LOOKUP' && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Riferimento anagrafica
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Il valore estratto sarà validato contro le voci dell'anagrafica selezionata.
                      </Typography>
                    </Box>
                    <RegistryFieldPicker
                      value={newAttribute.schema?.registryLookup || { registryId: '', field: '' }}
                      onChange={(cfg) => setNewAttribute((prev) => ({ ...prev, schema: { registryLookup: cfg } }))}
                      fetchRegistries={sharedFetchRegistries}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddAttribute}
                    disabled={!canAddAttribute}
                  >
                    Aggiungi Attributo
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Processi
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Seleziona i processi che possono essere eseguiti per questo tipo di codex e imposta il loro ordine di esecuzione:
              </Typography>
              
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableProcesses.map((process) => (
                  <Chip
                    key={process.id}
                    label={process.code}
                    onClick={() => handleProcessSelection(process.id)}
                    color={selectedProcesses.some(p => p.processId === process.id) ? "primary" : "default"}
                    variant={selectedProcesses.some(p => p.processId === process.id) ? "filled" : "outlined"}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
              
              {selectedProcesses.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Processi selezionati (ordine di esecuzione):
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                          <TableCell width="80px">Ordine</TableCell>
                          <TableCell>Codice</TableCell>
                          <TableCell>Descrizione</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Configurazione</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedProcesses
                          .sort((a, b) => a.processExecutionLevel - b.processExecutionLevel)
                          .map((processData) => {
                            const process = availableProcesses.find(p => p.id === processData.processId);
                            return (
                              <TableRow 
                                key={processData.processId}
                                sx={{ 
                                  cursor: 'move',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton
                                      size="small"
                                      disabled={processData.processExecutionLevel === 1}
                                      onClick={() => handleProcessReorder(processData.processId, processData.processExecutionLevel - 1)}
                                    >
                                      <KeyboardArrowUpIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body2">
                                      {processData.processExecutionLevel}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      disabled={processData.processExecutionLevel === selectedProcesses.length}
                                      onClick={() => handleProcessReorder(processData.processId, processData.processExecutionLevel + 1)}
                                    >
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                <TableCell>{process?.code}</TableCell>
                                <TableCell>{process?.description}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={process?.processTypeId || '-'}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell sx={{ minWidth: 250 }}>
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={8}
                                    size="small"
                                    placeholder="{}"
                                    value={configTexts[processData.processId] ?? ''}
                                    onChange={(e) => handleConfigChange(processData.processId, e.target.value)}
                                    error={Boolean(configErrors[processData.processId])}
                                    helperText={configErrors[processData.processId] || ''}
                                    InputProps={{
                                      sx: { fontFamily: 'monospace', fontSize: '0.8rem' }
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleProcessSelection(processData.processId)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {bindingsDialog && (
        <Dialog open onClose={() => setBindingsDialog(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            {`Binding anagrafiche — ${formData.attributes[bindingsDialog.index]?.code || ''}`}
          </DialogTitle>
          <DialogContent dividers>
            <RegistryBindingsEditor
              bindings={bindingsDialog.draft}
              onChange={(draft) => setBindingsDialog(prev => ({ ...prev, draft }))}
              showHeading={false}
              fetchRegistries={sharedFetchRegistries}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBindingsDialog(null)}>Annulla</Button>
            <Button
              variant="contained"
              onClick={handleConfirmBindings}
              disabled={!validateBindings(bindingsDialog.draft, statusOf).valid}
            >
              Conferma
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default NewCodexType; 